import { Tabs } from 'antd'
import { useReveal } from 'features/libra/comments/CommentsProvider'
import { useActiveComponent } from 'features/libra/components/ActiveComponentContext'
import {
  useNavigationGuard,
  useStepValidation,
} from 'features/libra/StepValidationContext'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { ComponentScopeProvider } from 'features/libra/workspace/ComponentScopeContext'
import { FC, ReactNode, useCallback, useEffect } from 'react'
import styled from 'styled-components'

/**
 * Minimal shape `UnitComponentTabs` needs to render a tab. Decoupled from
 * `GeckoComponent` so the caller (LibraView's `TabbedWorkspace`) can pass a
 * lightweight `{ id, name }` list without threading the whole `GeckoComponent`
 * shape through.
 */
export interface UnitComponentTabItem {
  id: string
  /** Display label. Falls back to `id` when missing. */
  name?: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

interface UnitComponentTabsProps {
  components: ReadonlyArray<UnitComponentTabItem>
  /**
   * Optional map of `componentId → inspectionId` to expose on
   * `ComponentScopeContext` for the active tab. Steps that need the
   * inspection (FFS asset model / binning / triage / min thickness) read
   * it via `useFfsTicketContext()`.
   */
  inspectionsByComponentId?: Record<string, string | undefined>
  children: ReactNode
}

/**
 * Tabs UI that lets a step swap which unit component is active. Mounts
 * `ComponentScopeProvider` around `children` so any descendant calling
 * `useComponentScope` / `useScopedFieldAccess` / `useFfsTicketContext`
 * resolves to the active tab.
 *
 * Tab switching routes through `useNavigationGuard()` so a `ConditionalForm`
 * (or any descendant that registers a save guard via `useRegisterSaveGuard`)
 * gets a chance to prompt Save / Discard / Cancel before the inner subtree
 * unmounts and drops buffered edits.
 */
export const UnitComponentTabs: FC<UnitComponentTabsProps> = ({
  components,
  inspectionsByComponentId,
  children,
}) => {
  // Session-scoped selection: lives ABOVE the step-keyed boundary (see
  // `ActiveComponentProvider`) so the active tab survives step navigation.
  const { activeComponentId: activeId, setActiveComponentId: setActiveId } =
    useActiveComponent()
  // Keep `activeId` valid. Two cases:
  //  - first mount / components loaded and no selection exists yet
  //  - the previously-active component disappears from the list (scope
  //    change, component renamed/deleted upstream)
  // In both cases default to the first available component.
  useEffect(() => {
    if (components.length === 0) return
    if (activeId && components.some((c) => c.id === activeId)) return
    setActiveId(components[0].id)
  }, [activeId, components, setActiveId])

  const { guardNavigation } = useNavigationGuard()
  const { setValidationResult } = useStepValidation()
  const handleChange = useCallback(
    (nextId: string) => {
      guardNavigation(() => {
        // Clear the previous tab's validation synchronously. Without this,
        // the Advance button briefly reflects the prior component's status
        // until the new tab's `useReportValidation` effect fires.
        setValidationResult(undefined)
        setActiveId(nextId)
      })
    },
    [guardNavigation, setValidationResult, setActiveId]
  )

  // Following a link to a component-scoped comment switches to its tab.
  const stepId = useActiveStepId()
  useReveal((anchor) => {
    if (anchor.stepId !== stepId) return
    const componentId = 'componentId' in anchor ? anchor.componentId : undefined
    if (!componentId || componentId === activeId) return
    if (components.some((c) => c.id === componentId)) handleChange(componentId)
  })

  // Empty component lists are the caller's concern (`TabbedWorkspace`
  // renders its own error screen and never mounts this with zero tabs).
  // `activeId` is undefined for at most one frame until the effect above
  // seeds the selection.
  if (!activeId || components.length === 0) {
    return null
  }

  const tabItems = components.map((c) => ({
    key: c.id,
    label: c.name || c.id,
  }))

  return (
    <Container>
      <Tabs
        size='small'
        activeKey={activeId}
        items={tabItems}
        onChange={handleChange}
      />
      {/* `key` on the body forces the inner subtree to remount on switch so
          useEffect cleanups (including `useRegisterSaveGuard`'s
          `setNavigationGuard(null)`) run before the next tab mounts. */}
      <Body key={activeId}>
        <ComponentScopeProvider
          componentId={activeId}
          inspectionId={inspectionsByComponentId?.[activeId]}>
          {children}
        </ComponentScopeProvider>
      </Body>
    </Container>
  )
}
